import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/xl70jip2_WYNN%20%282%29.png";

export default function Hero() {
  const navigate = useNavigate();
  return (
    <section data-testid="wynn-hero" className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.6]"
        style={{
          background:
            "radial-gradient(60% 50% at 78% 18%, rgba(255,150,200,0.35) 0%, rgba(7,6,8,0) 60%), radial-gradient(45% 40% at 12% 80%, rgba(120,200,230,0.30) 0%, rgba(7,6,8,0) 65%), radial-gradient(50% 45% at 50% 100%, rgba(180,140,230,0.22) 0%, rgba(7,6,8,0) 70%)",
        }}
      />
      <div aria-hidden className="absolute inset-0 wynn-grain opacity-40" />

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <div className="grid grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 md:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block h-1 w-8 bg-gradient-to-r from-[#FFB7C6] via-[#C7A8FF] to-[#A8E6FF]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/60 font-body">
                WYNN · Closed beta · Coming soon
              </span>
            </div>

            <h1
              data-testid="wynn-headline"
              className="font-display tracking-tighter leading-[0.92] text-[clamp(3rem,8.6vw,7rem)] font-medium text-[#F4F1EC]"
            >
              Creative <br />
              transformation, <br />
              <span className="italic font-light text-aurora">in your pocket</span>.
            </h1>

            <p className="mt-8 font-body text-[#F4F1EC]/75 text-lg md:text-xl leading-[1.7] max-w-2xl">
              WYNN is a small, intelligent companion for artists and makers. AI
              self-coaching, daily affirmations, meditations, sound healing, mood
              journaling, body practices and a quiet community — all in one calm
              pocket-sized space. Built by hand to walk with you back to your work.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-try-button"
                onClick={() => navigate("/app")}
                className="group h-12 px-7 font-body text-[12px] uppercase tracking-[0.22em] inline-flex items-center gap-2 bg-[#F4F1EC] text-[#070608] hover:bg-white transition-colors"
              >
                Try it now
                <ArrowRight
                  size={16}
                  strokeWidth={1.6}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
              <button
                data-testid="hero-waitlist-cta"
                onClick={() =>
                  document.querySelector("#join")?.scrollIntoView({ behavior: "smooth" })
                }
                className="h-12 px-7 font-body text-[12px] uppercase tracking-[0.22em] inline-flex items-center border border-[#F4F1EC]/40 text-[#F4F1EC] hover:border-[#FFB7C6] hover:text-[#FFB7C6] transition-colors"
              >
                Join the waitlist
              </button>
            </div>
            <p className="mt-5 font-body text-xs text-[#F4F1EC]/45 leading-relaxed">
              A free taste below — no sign-up. Beta testers keep founding-member
              pricing for life.
            </p>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="col-span-12 md:col-span-5 flex justify-center md:justify-end"
            data-testid="wynn-logo-card"
          >
            <div className="relative wynn-float">
              <div
                aria-hidden
                className="absolute inset-0 blur-3xl opacity-70 -z-10"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, rgba(255,170,210,0.55) 0%, rgba(170,200,240,0.35) 45%, rgba(7,6,8,0) 75%)",
                }}
              />
              <img
                src={LOGO}
                alt="WYNN app icon"
                className="relative w-[280px] md:w-[360px] h-auto select-none drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]"
                draggable="false"
              />
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
